import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  House, Columns, ChatCircleText, Vault, DotsThree, Desk, Tray, Users, Envelope,
} from '@phosphor-icons/react';
import { navFor } from '../../lib/navigation';

/**
 * Arcanum 2.0 primary navigation. Built in Phase 1, mounted in Phase 3.
 *
 * It renders what navFor() returns and decides nothing itself -- who sees
 * which item is tested there, not here. Rules from 02_GLOBAL_VISUAL_SYSTEM:
 * visible labels, touch targets of at least 44px, safe-area padding, and an
 * active state that is gold text plus a quiet tinted surface. No pulsing,
 * shimmer or bounce.
 *
 * `activeKey` lets a child screen keep its originating destination lit --
 * Secure Capsule opened from Room keeps Room active. Without it the active
 * item is inferred from the path, longest match first.
 */

const ICONS = {
  room: House,
  commons: Columns,
  support: ChatCircleText,
  vault: Vault,
  more: DotsThree,
  desk: Desk,
  queue: Tray,
  members: Users,
  invites: Envelope,
};

function inferActive(items, pathname) {
  const hit = [...items]
    .sort((a, b) => b.to.length - a.to.length)
    .find((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
  return hit ? hit.key : null;
}

const BottomNav = ({ user, activeKey }) => {
  const { pathname } = useLocation();
  const { primary } = navFor(user);
  const active = activeKey || inferActive(primary, pathname);

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-sm"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="bottom-nav"
    >
      <ul
        className="mx-auto grid max-w-xl"
        style={{ gridTemplateColumns: `repeat(${primary.length}, minmax(0, 1fr))` }}
      >
        {primary.map((item) => {
          const Icon = ICONS[item.key] || DotsThree;
          const on = item.key === active;
          return (
            <li key={item.key} className="min-w-0">
              <Link
                to={item.to}
                aria-current={on ? 'page' : undefined}
                data-testid={`nav-${item.key}`}
                className={[
                  'mx-1 my-1.5 flex min-h-[44px] flex-col items-center justify-center gap-0.5 rounded-lg px-1',
                  'text-[11px] font-medium tracking-wide transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  on ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground',
                ].join(' ')}
              >
                <Icon className="h-5 w-5 shrink-0" weight={on ? 'fill' : 'regular'} aria-hidden="true" />
                {/* Never truncated to an ellipsis: "The Commons" must read whole. */}
                <span className="max-w-full text-center leading-tight" style={{ overflowWrap: 'anywhere' }}>
                  {item.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default BottomNav;
